import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';

import axios, {
  isDefaultResponse,
  parseAxiosError,
} from '@/portainer/services/axios/axios';
import PortainerError from '@/portainer/error';

import { AuthTypeOption } from '../../account/git-credentials/types';

interface Creds {
  username?: string;
  password?: string;
  authorizationType?: AuthTypeOption;
  gitCredentialId?: number;
}
export interface CheckRepoOptions {
  creds?: Creds;
  force?: boolean;
  tlsSkipVerify?: boolean;
  createdFromCustomTemplateId?: number;
  stackId?: number;
  fromEdgeStack?: boolean;
}

export function useCheckRepo(
  url: string,
  options: CheckRepoOptions,
  {
    enabled,
    onSettled,
  }: { enabled?: boolean; onSettled?(isValid?: boolean): void } = {}
) {
  return useQuery<Awaited<ReturnType<typeof checkRepo>>, PortainerError>(
    ['git_repo_valid', url, options],
    () => checkRepo(url, options),
    {
      enabled: !!url && enabled,
      onSettled,
      retry: false,
    }
  );
}

export async function checkRepo(
  repository: string,
  { force, creds = {}, ...options }: CheckRepoOptions
): Promise<boolean> {
  try {
    await axios.post<string[]>(
      '/gitops/repo/refs',
      { repository, ...options, ...creds },
      force ? { params: { force } } : {}
    );
    return true;
  } catch (error) {
    throw parseAxiosError(error, '', (axiosError: AxiosError) => {
      let details = isDefaultResponse(axiosError.response?.data)
        ? axiosError.response?.data.details || ''
        : '';

      // If no credentials were provided alter error from git to indicate repository is not found or is private
      if (
        (!(creds.username && creds.password) || !creds.gitCredentialId) &&
        details === 'Authentication required: Repository not found.'
      ) {
        details =
          'Git repository could not be found or is private, please ensure that the URL is correct or credentials are provided.';
      }

      const error = new Error(details);
      return { error, details };
    });
  }
}
